import type { useFetchRestrictionsRESTObjRestrictionsRESTQuery as RestrictionsQueryData } from '../RestrictionsREST/__types__/useFetchRestrictionsRESTObjRestrictionsRESTQuery.ts';

/**
 * GqlRestrictionsInput type matches the GraphQL input type for page/blog mutations.
 * This is used by the confluence_updatePage and confluence_updateBlogPost mutations.
 */
export type PageUserRestrictionInput = {
	id: string;
};

export type PageGroupRestrictionInput = {
	id?: string | null;
	name: string;
};

export type PageRestrictionInput = {
	user?: PageUserRestrictionInput[] | null;
	group?: PageGroupRestrictionInput[] | null;
};

export type ConfluenceContentPermissionPrincipalInput = {
	id: string;
	type: 'USER' | 'GROUP';
};

export type ConfluenceContentPermissionsInput = {
	view?: ConfluenceContentPermissionPrincipalInput[];
	edit?: ConfluenceContentPermissionPrincipalInput[];
};

export type ConfluenceContentRestrictionStateInput =
	| 'OPEN'
	| 'EDIT_RESTRICTED'
	| 'VIEW_RESTRICTED'
	| '%future added value';

export type GqlRestrictionsInput = {
	includeInvites?: boolean | null;
	read?: PageRestrictionInput | null;
	update?: PageRestrictionInput | null;
	permissions?: ConfluenceContentPermissionsInput | null;
	restrictionState?: ConfluenceContentRestrictionStateInput | null;
};

class InvalidGraphQLRestrictionsError extends Error {
	constructor() {
		super('Invalid restrictions were fetched from GraphQL.');
	}
}

/**
 * Transforms GraphQL query result directly to GqlRestrictionsInput format.
 * This function is used when 'use_graphql_to_publish_pages_and_blogs' experiment is enabled.
 *
 * @param queryData - The GraphQL query result from useFetchRestrictionsRESTObjRestrictionsRESTQuery
 * @returns The restrictions object in GraphQL GqlRestrictionsInput format, or undefined if input is invalid
 */
export function convertRestrictionsToGqlRestrictionsInput(
	queryData: RestrictionsQueryData | undefined,
): GqlRestrictionsInput {
	if (!queryData) {
		return {};
	}

	const readRestriction = queryData?.content?.nodes?.[0]?.restrictions?.read?.restrictions;
	const updateRestriction = queryData?.content?.nodes?.[0]?.restrictions?.update?.restrictions;

	if (readRestriction === undefined || updateRestriction === undefined) {
		throw new InvalidGraphQLRestrictionsError();
	}

	const result: GqlRestrictionsInput = {};

	const readUserNodes = readRestriction?.userWithRestrictions?.nodes ?? [];
	const readGroupNodes = readRestriction?.groupWithRestrictions?.nodes ?? [];

	result.read = {
		user: readUserNodes
			.filter((user): user is NonNullable<typeof user> => user !== null)
			.map((user) => ({
				id: user.accountId ?? '',
			})),
		group: readGroupNodes
			.filter((group): group is NonNullable<typeof group> => group !== null)
			.map((group) => ({
				name: group.name ?? '',
				id: group.id || undefined,
			})),
	};

	const updateUserNodes = updateRestriction?.userWithRestrictions?.nodes ?? [];
	const updateGroupNodes = updateRestriction?.groupWithRestrictions?.nodes ?? [];

	result.update = {
		user: updateUserNodes
			.filter((user): user is NonNullable<typeof user> => user !== null)
			.map((user) => ({
				id: user.accountId ?? '',
			})),
		group: updateGroupNodes
			.filter((group): group is NonNullable<typeof group> => group !== null)
			.map((group) => ({
				name: group.name ?? '',
				id: group.id || undefined,
			})),
	};

	return result;
}

/**
 * This function returns true if only one user has access
 * to read and update the provided restrictions object.
 */
export const restrictionsArePrivateToYouOnlyGql = (
	restrictions: GqlRestrictionsInput | undefined,
): boolean => {
	if (!restrictions) {
		return false;
	}

	const onlyOneUserCanRead = (restrictions.read?.user?.length ?? 0) === 1;
	const noGroupsCanRead = (restrictions.read?.group?.length ?? 0) === 0;
	const onlyOneUserCanUpdate = (restrictions.update?.user?.length ?? 0) === 1;
	const noGroupsCanUpdate = (restrictions.update?.group?.length ?? 0) === 0;

	return onlyOneUserCanRead && noGroupsCanRead && onlyOneUserCanUpdate && noGroupsCanUpdate;
};
